import { InjectionToken } from "@angular/core";
import { AUC_ENVIRONMENT } from "@auction-ui/environments/environment";
import { AucRouteNames } from "@auction-ui/route.names";
import { faFacebook, faLinkedinIn } from "@fortawesome/free-brands-svg-icons";
import { faHammer, faMagnifyingGlass, faTruck, faX } from "@fortawesome/free-solid-svg-icons";
import { NgxMatDateFormats } from "@ngxmc/datetime-picker";
import { AFConfig } from "@pattonair/auction-framework";
import { MF_AUTH_CONFIG_DEFAULT, MF_INPUT_DEFAULT_CONFIG, MF_LOADING_DEFAULT_CONFIG, MF_MODEL_VALUE_OUTPUT_FORMATTERS_CONFIG_DEFAULT, MF_PORTALS_DEFAULT_CONFIG, MF_TABLE_DEFAULT_CONFIG, MF_VIEW_MANAGER_DEFAULT_CONFIG, MfAppConfig, MfAuthConfig, MfAuthLandingConfig, MfAuthRouteNames, MfIcon, MfIconTypes, MfInputConfig, MfLoadingConfig, MfModelValueDefaultOptions, MfModelValueTypes, MfPortalsConfig, MfPortalsTableDataSource, MfPortalsTableSelectionManager, MfTableConfig, MfTableDataReloadTypes, MfTableFilterModeTypes, MfTableHeaderItem } from "@pattonair/material-framework";

export const AUC_AUTH_CODE_FLOW_CONFIG: MfAuthConfig = {
  ...MF_AUTH_CONFIG_DEFAULT,
  clientId: AUC_ENVIRONMENT.authConfig.clientId,
  flow: "code",
  issuer: AUC_ENVIRONMENT.authConfig.issuer,
  postLogoutRedirectUri: `${window.location.origin}/${MfAuthRouteNames.partNameLoggedOut}`,
  requireHttps: false,
  responseType: "code",
  scope: AUC_ENVIRONMENT.authConfig.scope,
  showDebugInformation: true,
  skipIssuerCheck: true,
  strictDiscoveryDocumentValidation: false,
  useAutomaticSilentRefresh: false,
  authRedirect: {
    path: AucRouteNames.routeAuctionsList,
    delay: 2000,
  }
};

export const AUC_AF_CONFIG: AFConfig = {
  auctionRootUrl: `${AUC_ENVIRONMENT.auctionManagementRootUrl}`,
};

export const AUC_CONFIG_TOKEN = new InjectionToken<AucLinksConfig>("AucConfig");
export const AUC_DEFAULT_CONFIG: AucConfig = {
  name: "Auction"
};

export const AUC_APP_CONFIG: MfAppConfig = {
  name: AUC_DEFAULT_CONFIG.name,
  homeRoute: AucRouteNames.routeAuctionsList,
};

export type AucConfig = {
  name: string;
}

export const AUC_ICONS_CONFIG_TOKEN = new InjectionToken<AucIconsConfig>("AucIconsConfig");
export const AUC_ICONS_DEFAULT_CONFIG: AucIconsConfig = {
  auctionIcon: { type: MfIconTypes.fontAwesome, icon: faHammer },
  bidsIcon: { type: MfIconTypes.fontAwesome, icon: faMagnifyingGlass },
  facebookIcon: { type: MfIconTypes.fontAwesome, icon: faFacebook },
  linkedInIcon: { type: MfIconTypes.fontAwesome, icon: faLinkedinIn },
  wonIcon: { type: MfIconTypes.fontAwesome, icon: faTruck },
  xIcon: { type: MfIconTypes.fontAwesome, icon: faX },
};

export type AucIconsConfig = {
  auctionIcon: MfIcon;
  bidsIcon: MfIcon;
  facebookIcon: MfIcon;
  linkedInIcon: MfIcon;
  wonIcon: MfIcon;
  xIcon: MfIcon;
}

export const AUC_LINKS_CONFIG_TOKEN = new InjectionToken<AucLinksConfig>("AucLinksConfig");
export const AUC_LINKS_DEFAULT_CONFIG: AucLinksConfig = {
  privacyPolicyUrl: "https://www.incora.com/privacy-policy/",
  cookiesSettingsUrl: "https://www.incora.com/cookie-policy/",
  termsOfServiceUrl: "https://www.incora.com/resources/terms-conditions/",
  facebookUrl: "https://www.facebook.com/incoraglobal/",
  xUrl: "https://x.com/incoraglobal",
  linkedInUrl: "https://www.linkedin.com/company/incoraglobal/"
};

export type AucLinksConfig = {
  privacyPolicyUrl: string;
  cookiesSettingsUrl: string;
  termsOfServiceUrl: string;
  facebookUrl: string;
  xUrl: string;
  linkedInUrl: string;
}

export const AUC_PORTALS_DEFAULT_CONFIG: MfPortalsConfig = {
  ...MF_PORTALS_DEFAULT_CONFIG,
  httpRetry: {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    409: { retryCount: 1 }
  },
};

export const AUC_TABLE_CONFIG: MfTableConfig = {
  ...MF_TABLE_DEFAULT_CONFIG,
  dataAutoReload: [MfTableDataReloadTypes.paginationChange, MfTableDataReloadTypes.sortChange, MfTableDataReloadTypes.viewChange, MfTableDataReloadTypes.filterChange],
  filter: {
    ...MF_TABLE_DEFAULT_CONFIG.filter,
    mode: MfTableFilterModeTypes.rowHeader,
  },
  header: {
    ...MF_TABLE_DEFAULT_CONFIG.header,
    layout: {
      left: [],
      right: [
        MfTableHeaderItem.orderShowHideButton,
        MfTableHeaderItem.viewManager,
      ]
    }
  },
  footer: {
    ...MF_TABLE_DEFAULT_CONFIG.footer,
    pagination: {
      ...MF_TABLE_DEFAULT_CONFIG.footer.pagination,
      showRange: false,
      pageSize: 10,
    }
  },
  dataSourceType: MfPortalsTableDataSource,
  selectionManagerType: MfPortalsTableSelectionManager,
};

export const AUC_VIEW_MANAGER_CONFIG = {
  ...MF_VIEW_MANAGER_DEFAULT_CONFIG,
  controls: {
    ...MF_VIEW_MANAGER_DEFAULT_CONFIG.controls,
    showSelect: false,
    showDefaultToggle: false,
    showSave: false,
    showSaveAs: false,
    showDelete: false
  }
};

export const AUC_DATE_FORMATS: NgxMatDateFormats = {
  parse: {
    dateInput: "l, LTS"
  },
  display: {
    //dateInput: "l, LTS",
    dateInput: "DD MMM YYYY",
    monthYearLabel: "MMM YYYY",
    dateA11yLabel: "LL",
    monthYearA11yLabel: "MMMM YYYY"
  }
};

export const AUC_LOADING_CONFIG: MfLoadingConfig = {
  ...MF_LOADING_DEFAULT_CONFIG,
  defaultIcon: "data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22133%22%20height%3D%2244%22%20viewBox%3D%220%200%20133%2044%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%3E%3Cg%3E%3Cpath%20fill%3D%22%236DC5A9%22%20d%3D%22M34.501%2043.962L85.401%2043.962%2085.401%2041.19%2034.501%2041.19z%22%20transform%3D%22translate(-47%20-54)%20translate(47%2054)%22%2F%3E%3Cpath%20fill%3D%22%23182C55%22%20d%3D%22M47%209.25c6.101%200%2010.15%203.5%2011.052%209h-4.1c-.6-3.5-3.051-8.02-6.951-8.02-5.5%200-8.35%206.92-8.35%2012.62%200%205.7%202.85%2012.499%208.35%2012.499%204.2%200%206.7-4.7%207.05-8.7h4c-.55%206.201-4.6%209.801-11.05%209.801-7.7%200-12.5-6.1-12.5-13.6S39.3%209.25%2047%209.25zm25.951%200c7.75%200%2012.45%206.1%2012.45%2013.6s-4.7%2013.6-12.45%2013.6c-7.7%200-12.5-6.1-12.5-13.6s4.8-13.6%2012.5-13.6zm42.7.15c5.5%200%209.551%202.05%209.551%208.2v12.3c0%202.1.5%203.25%202.75%202.75h.15v2.95c-.5.2-1.15.4-2.2.4-2.65%200-4.05-1.2-4.45-3.85h-.05c-1.55%202-4.2%204.1-8.6%204.1-4.9%200-8.75-2.3-8.75-7.2%200-6.1%204.8-7.35%2011.25-8.35%203.65-.55%206.2-.9%206.2-3.7%200-2.9-2.189-6.46-5.889-6.46-4.2%200-6.211%203.71-6.361%207.61h-3.9c.15-5%203.45-8.75%2010.3-8.75zm-111.6.5v25.85H.008V9.9H4.05zm17.85-.6c5.4%200%208.7%202.95%208.7%208.3v18.15h-4.05v-17.8c0-3.35-2.202-5.25-5.85-5.25-4.1%200-7.101%202.9-7.101%207.15v15.9H9.55V9.9h4.05v5.241h.1c1.85-2.45%204.45-5.841%208.2-5.841zm79.4.3c.75%200%201.15.1%201.65.3v3.9h-.15c-.6-.2-1-.25-1.8-.25-4%200-7.5%203-7.5%207.85v14.35h-4.05V9.9h4.05v6.858h.1c1.55-2.5%204.25-7.158%207.7-7.158zm-28.35.63c-5.5%200-8.35%206.92-8.35%2012.62%200%205.7%202.85%2012.436%208.35%2012.436s8.3-6.737%208.3-12.436c0-5.7-2.8-12.62-8.3-12.62zm48.6%2011.57c-1.349.85-3.7%201.5-5.9%201.9-4.05.75-7.3%201.45-7.3%205.25%200%203.45%202.684%206.336%205.335%206.336%205.4%200%207.866-6.086%207.866-9.086v-4.4zm9.1-12.19l.425%201.525h.017l.434-1.526h.936v2.42h-.624v-1.57h-.018l-.476%201.57h-.53l-.468-1.57h-.017v1.57h-.615v-2.42h.936zm-1.292%200v.554h-.711v1.864h-.677v-1.864h-.71V9.61h2.098zM4.05%200v5H.01V0h4.04z%22%20transform%3D%22translate(-47%20-54)%20translate(47%2054)%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E"
};

export const AUC_INPUT_DEFAULT_CONFIG: MfInputConfig = {
  ...MF_INPUT_DEFAULT_CONFIG,
  mouseWheelDisabled: true,
};

export const AUC_AUTH_LANDING_CONFIG: MfAuthLandingConfig = {
  loggedOut: {
    backgroundImageUrl: "assets/eComm-Splash-1920x1080.jpg",
    showSlogan: true,
    sloganHeaderText: "Incora Portal",
    sloganText: "We simplify the complex.",
    color: "white",
  },
  login: {
    backgroundImageUrl: "assets/eComm-Splash-1920x1080.jpg",
    showSlogan: true,
    sloganHeaderText: "Incora Portal",
    sloganText: "We simplify the complex.",
    color: "white",
    showLoginButton: true,
    loginRedirectUrl: AucRouteNames.routeAuctionsList,
  }
};

export const AUC_MODEL_VALUE_OUTPUT_FORMATTERS_CONFIG_DEFAULT: MfModelValueDefaultOptions = {
  ...MF_MODEL_VALUE_OUTPUT_FORMATTERS_CONFIG_DEFAULT,
  [MfModelValueTypes.currency]: {
    digitsInfo: "1.2-2",
    currencyCode: "",
    display: "",
  },
};